import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { DatabaseMetaInfo } from '../../../../types/electron';
// BUNDLE ISOLATION: the ONLY spatial-decode import reachable from the eager grid graph is the
// header sniffer (spatialSniff.ts). Never import decodeSpatialValue / wkx / the lazy chunk here.
import { sniffSpatialValue } from '../../spatial/decode/spatialSniff';

/**
 * How a column relates to spatial geometry.
 *
 * - `native-spatial` — the column's engine type is a real spatial type
 *   (PostGIS `geometry`/`geography`, MySQL `point`/`geometry`/…, MSSQL
 *   `geometry`/`geography`, Oracle `SDO_GEOMETRY`, SQLite declared geometry
 *   types), or (SQLite only) a BLOB/affinity-less column whose loaded values
 *   sniff as SpatiaLite/GeoPackage blobs (`sniffSpatialBlobColumns`).
 * - `declared-spatial` — the user has declared a non-native column (a WKT/
 *   GeoJSON text column or a lat/lng pair) as spatial via MetaInfo. **T18**
 *   populates this arm; T4 never returns it.
 * - `spatial-declarable` — a column a user *could* declare as spatial but
 *   hasn't. **T18/T19** populate this arm; T4 never returns it.
 * - `not-spatial` — none of the above.
 */
export type SpatialColumnCategory =
  | 'native-spatial'
  | 'declared-spatial'
  | 'spatial-declarable'
  | 'not-spatial';

export type SpatialColumnInput = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  name: string;
  /** Engine-specific column data type string, e.g. `geometry(Point,4326)`, `SDO_GEOMETRY`, `BLOB`. */
  dataType: string;
};

/**
 * Lowercased base type with parenthesized modifiers stripped, mirroring
 * `jsonColumnCategorization.parseDataType`. PostGIS presents `geometry(Point,
 * 4326)` on the TE metadata path (SPATIAL-FACTS a4) — we categorize by the
 * bare base name `geometry`, not the typmod suffix or `USER-DEFINED`/udt_name.
 * Runtime-tolerant: `dataType` is typed `string` but callers feed
 * `column.dataType` which can be `undefined` (metadata gaps); a missing type
 * degrades to an empty base rather than throwing on the per-render hot path.
 */
function parseSpatialBaseType(raw: string): string {
  const lowered = (typeof raw === 'string' ? raw : '').trim().toLowerCase();
  const match = lowered.match(/^([a-z0-9_ ]+?)\s*(?:\(\s*[^)]*\s*\))?(?:\s.*)?$/u);
  return (match?.[1] ?? lowered).trim().replace(/\s+/gu, ' ');
}

/**
 * Native spatial base types per engine. Kusto has none. Oracle exposes both
 * the adapter-mapped `geometry` (getOracleTypeName maps SDO objects) and the
 * raw `sdo_geometry` type name. SQLite lists the declared geometry type names
 * GeoPackage/SpatiaLite tables use; BLOB/affinity-less columns are handled
 * separately by `sniffSpatialBlobColumns`.
 */
const NATIVE_SPATIAL_TYPES: Record<DatabaseEngineId, ReadonlySet<string>> = {
  pgsql: new Set(['geometry', 'geography']),
  mysql: new Set([
    'geometry',
    'point',
    'linestring',
    'polygon',
    'multipoint',
    'multilinestring',
    'multipolygon',
    'geometrycollection',
  ]),
  mssql: new Set(['geometry', 'geography']),
  oracle: new Set(['geometry', 'sdo_geometry']),
  sqlite: new Set([
    'geometry',
    'point',
    'linestring',
    'polygon',
    'multipoint',
    'multilinestring',
    'multipolygon',
    'geometrycollection',
  ]),
  kusto: new Set(),
  azurestorage: new Set(),
  mock: new Set(['geometry', 'geography']),
};

function isNativeSpatialType(engine: DatabaseEngineId, base: string): boolean {
  return NATIVE_SPATIAL_TYPES[engine].has(base);
}

/**
 * True when the user has declared this column as spatial coordinates
 * (`metaInfo.spatialColumns`). Identity is `(schema, table, column)`; for a
 * `lat-lng-pair` the declaration's `column` is the LAT column, so ONLY the lat
 * column matches (the lng column stays plain — the logical map cell is the lat
 * cell, A8/CD5). Mirrors the JSON/enum declared-matching path.
 */
function isDeclaredSpatial(
  column: SpatialColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): boolean {
  if (!metaInfo) return false;
  return metaInfo.spatialColumns.some(
    (d) =>
      d.schema === column.schema &&
      d.table === column.table &&
      d.column === column.name,
  );
}

/**
 * TEXT-typed base per `filterUtils.categorizeDataType`'s string bucket
 * (char/text/varchar/nvarchar/nchar/ntext). Duplicated locally (rather than
 * imported) to avoid a circular dependency — `filterUtils.ts` imports FROM this
 * module, and `spatialDeclarableSuggest.ts` keeps its own copy for the same
 * reason (that split-out module only takes a TYPE-ONLY import back from here).
 */
function isTextTypedColumn(dataType: string): boolean {
  const t = (typeof dataType === 'string' ? dataType : '').trim().toLowerCase();
  return (
    t.includes('char') ||
    t.includes('text') ||
    t.includes('varchar') ||
    t.includes('nvarchar') ||
    t.includes('nchar') ||
    t.includes('ntext')
  );
}

// Name-arm (zero I/O), single-column: a TEXT column whose NAME hints it holds
// WKT/GeoJSON text. Deliberately unanchored/substring (spec §3.4) — matches
// `geom_wkt`, `shape_geom`, `the_geojson_blob`, etc.
const DECLARABLE_TEXT_NAME_RE = /wkt|geojson|geom/i;

/**
 * Categorize a column by its spatial-ness. **Native still wins** — a column
 * whose engine type is a real spatial type is `native-spatial` even if a
 * declaration also exists; only a NON-native column with a MetaInfo declaration
 * (Task 18) becomes `declared-spatial`. The `spatial-declarable` arm (T19) here
 * covers ONLY the single-column, zero-I/O name heuristic (a TEXT column named
 * like `geom_wkt`); the multi-column pair heuristic and the value-sampling
 * heuristic need sibling/row context this per-column function doesn't have —
 * see `suggestSpatialPairs` / `sampleDeclarableTextColumns` below.
 */
export function categorizeSpatialColumn(
  column: SpatialColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): SpatialColumnCategory {
  if (isNativeSpatialType(column.engine, parseSpatialBaseType(column.dataType))) {
    return 'native-spatial';
  }
  if (isDeclaredSpatial(column, metaInfo)) {
    return 'declared-spatial';
  }
  if (isTextTypedColumn(column.dataType) && DECLARABLE_TEXT_NAME_RE.test(column.name)) {
    return 'spatial-declarable';
  }
  return 'not-spatial';
}

/**
 * SQLite is a BLOB affinity when its declared type contains `blob` or is empty
 * (no affinity). GeoPackage/SpatiaLite geometry columns are routinely declared
 * this way, so name-based categorization misses them — hence the value sniff.
 */
function isSqliteBlobAffinity(dataType: string): boolean {
  const t = (typeof dataType === 'string' ? dataType : '').trim().toLowerCase();
  return t === '' || t.includes('blob');
}

const MAX_BLOB_SNIFF_ROWS = 20;

/**
 * SQLite-only BLOB promotion (spec §3.1). For each BLOB / affinity-less column,
 * sniff the already-loaded sample window (zero I/O — no decode, just the eager
 * header sniffer). A column is promoted to native-spatial iff at least one
 * non-null value sniffs as spatial AND no non-null value fails the sniff (the
 * false-positive guard — a mixed column of geometry + garbage is NOT promoted).
 * Returns the set of promoted column `name`s.
 */
export function sniffSpatialBlobColumns(
  columns: SpatialColumnInput[],
  sampleRows: Record<string, unknown>[],
): Set<string> {
  const promoted = new Set<string>();
  for (const column of columns) {
    if (column.engine !== 'sqlite') continue;
    if (!isSqliteBlobAffinity(column.dataType)) continue;

    let sawSpatial = false;
    let sawFailure = false;
    const limit = Math.min(sampleRows.length, MAX_BLOB_SNIFF_ROWS);
    for (let i = 0; i < limit; i += 1) {
      const value = sampleRows[i]?.[column.name];
      if (value === null || value === undefined) continue;
      if (sniffSpatialValue('sqlite', value, column.dataType)) {
        sawSpatial = true;
      } else {
        sawFailure = true;
        break; // one non-null non-spatial value disqualifies the column
      }
    }
    if (sawSpatial && !sawFailure) promoted.add(column.name);
  }
  return promoted;
}

// T19 — declarable suggestion heuristic (pair name-arm + value-sampling arm).
// Split into a sibling module to keep this file ≤200 lines; re-exported here
// so callers use one import path for every spatial categorization concern.
export {
  suggestSpatialPairs,
  findSpatialPairPartner,
  sampleDeclarableTextColumns,
} from './spatialDeclarableSuggest';
